<template>
  <HrPageLayout
    title="Last Expense"
    subtitle="Online claim submission, approval workflow, payment tracking"
    icon="🕯"
    :breadcrumb-items="[{ label: 'Home', route: '/dashboard' }, { label: 'Claims & Payments' }, { label: 'Last Expense' }]"
  >
    <div class="director-module">
      <div class="actions-row">
        <button type="button" class="hr-btn hr-btn-primary" @click="openSubmitModal">Submit claim</button>
        <select v-model="filterStatus" @change="loadClaims">
          <option value="">All statuses</option>
          <option value="Submitted">Submitted</option>
          <option value="Approved">Approved</option>
          <option value="Paid">Paid</option>
        </select>
      </div>
      <div class="table-wrap card-section">
        <table class="table table-bordered table-sm">
          <thead>
            <tr>
              <th>Deceased</th>
              <th>Department</th>
              <th>Claimant</th>
              <th>Relationship</th>
              <th>Amount (KES)</th>
              <th>Death date</th>
              <th>Status</th>
              <th>Approved</th>
              <th>Payment ref</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="c in claims" :key="c.id">
              <td>{{ c.deceased_name || c.deceased_national_id || '–' }}</td>
              <td>{{ c.department_id }}</td>
              <td>{{ c.claimant_name }}</td>
              <td>{{ c.claimant_relationship || '–' }}</td>
              <td>{{ formatNum(c.claim_amount) }}</td>
              <td>{{ c.death_date || '–' }}</td>
              <td>{{ c.status }}</td>
              <td>{{ c.approved_at ? 'Yes' : 'No' }}</td>
              <td>{{ c.payment_reference || '–' }}</td>
              <td>
                <button v-if="c.status === 'Submitted'" type="button" class="hr-btn hr-btn-sm hr-btn-success" @click="approveClaim(c.id)">Approve</button>
                <button v-if="c.status === 'Approved'" type="button" class="hr-btn hr-btn-sm" @click="openPaymentModal(c)">Record payment</button>
              </td>
            </tr>
            <tr v-if="!claims.length"><td colspan="10" class="text-muted">No claims</td></tr>
          </tbody>
        </table>
      </div>

      <div v-if="showSubmitModal" class="modal-overlay" @click.self="showSubmitModal = false">
        <div class="modal-card">
          <h3>Submit last expense claim</h3>
          <div class="form-group">
            <label>Deceased name</label>
            <input v-model="claimForm.deceased_name" type="text">
          </div>
          <div class="form-group">
            <label>Deceased national ID</label>
            <input v-model="claimForm.deceased_national_id" type="text">
          </div>
          <div class="form-group">
            <label>Department</label>
            <select v-model="claimForm.department_id">
              <option value="">Select</option>
              <option v-for="d in departments" :key="d.id" :value="d.departmentID || d.id">{{ d.departmentName }}</option>
            </select>
          </div>
          <div class="form-group">
            <label>Claimant name</label>
            <input v-model="claimForm.claimant_name" type="text" required>
          </div>
          <div class="form-group">
            <label>Relationship</label>
            <input v-model="claimForm.claimant_relationship" type="text">
          </div>
          <div class="form-group">
            <label>Claimant phone</label>
            <input v-model="claimForm.claimant_phone" type="text">
          </div>
          <div class="form-group">
            <label>Claim amount (KES)</label>
            <input v-model.number="claimForm.claim_amount" type="number" step="0.01">
          </div>
          <div class="form-group">
            <label>Death date</label>
            <input v-model="claimForm.death_date" type="date">
          </div>
          <div class="modal-actions">
            <button type="button" class="hr-btn hr-btn-secondary" @click="showSubmitModal = false">Cancel</button>
            <button type="button" class="hr-btn hr-btn-primary" :disabled="saving" @click="submitClaim">Submit</button>
          </div>
        </div>
      </div>

      <div v-if="showPaymentModal" class="modal-overlay" @click.self="showPaymentModal = false">
        <div class="modal-card">
          <h3>Record payment</h3>
          <div class="form-group">
            <label>Payment reference</label>
            <input v-model="paymentForm.payment_reference" type="text">
          </div>
          <div class="form-group">
            <label>Payment date</label>
            <input v-model="paymentForm.payment_date" type="date">
          </div>
          <div class="modal-actions">
            <button type="button" class="hr-btn hr-btn-secondary" @click="showPaymentModal = false">Cancel</button>
            <button type="button" class="hr-btn hr-btn-primary" :disabled="saving" @click="savePayment">Save</button>
          </div>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { execute, authUrl } from "@/api";

export default {
  name: "LastExpense",
  components: { HrPageLayout },
  data() {
    return {
      claims: [],
      departments: [],
      filterStatus: "",
      showSubmitModal: false,
      showPaymentModal: false,
      saving: false,
      claimForm: {
        deceased_name: "",
        deceased_national_id: "",
        department_id: "",
        claimant_name: "",
        claimant_relationship: "",
        claimant_phone: "",
        claim_amount: 0,
        death_date: ""
      },
      paymentForm: { id: null, payment_reference: "", payment_date: "" }
    };
  },
  created() {
    this.getDepartments();
    this.loadClaims();
  },
  methods: {
    getDepartments() {
      const fd = new FormData();
      fd.append("function", "getDepartments");
      execute(fd, authUrl).then((res) => {
        if (res.data && res.data.success && res.data.data && res.data.data.departments)
          this.departments = res.data.data.departments;
      }).catch(() => {});
    },
    loadClaims() {
      const fd = new FormData();
      fd.append("function", "getLastExpenseClaims");
      execute(fd, authUrl).then((res) => {
        let list = (res.data && res.data.success && res.data.data && res.data.data.claims) ? res.data.data.claims : [];
        if (this.filterStatus) list = list.filter((c) => c.status === this.filterStatus);
        this.claims = list;
      }).catch(() => { this.claims = []; });
    },
    formatNum(n) {
      if (n == null) return "0";
      return Number(n).toLocaleString("en-KE", { minimumFractionDigits: 2 });
    },
    openSubmitModal() {
      this.claimForm = { deceased_name: "", deceased_national_id: "", department_id: "", claimant_name: "", claimant_relationship: "", claimant_phone: "", claim_amount: 0, death_date: "" };
      this.showSubmitModal = true;
    },
    submitClaim() {
      if (!this.claimForm.claimant_name) return;
      this.saving = true;
      const fd = new FormData();
      fd.append("function", "submitLastExpenseClaim");
      Object.keys(this.claimForm).forEach((k) => fd.append(k, this.claimForm[k] == null ? "" : this.claimForm[k]));
      execute(fd, authUrl).then((res) => {
        this.saving = false;
        if (res.data && res.data.success) {
          this.showSubmitModal = false;
          this.loadClaims();
        } else {
          alert(res.data && res.data.message ? res.data.message : "Submit failed");
        }
      }).catch(() => { this.saving = false; });
    },
    approveClaim(id) {
      const fd = new FormData();
      fd.append("function", "approveLastExpenseClaim");
      fd.append("id", id);
      execute(fd, authUrl).then((res) => {
        if (res.data && res.data.success) this.loadClaims();
        else alert(res.data && res.data.message ? res.data.message : "Approval failed");
      }).catch(() => {});
    },
    openPaymentModal(c) {
      this.paymentForm = { id: c.id, payment_reference: c.payment_reference || "", payment_date: c.payment_date || new Date().toISOString().slice(0, 10) };
      this.showPaymentModal = true;
    },
    savePayment() {
      if (!this.paymentForm.id) return;
      this.saving = true;
      const fd = new FormData();
      fd.append("function", "updateLastExpensePayment");
      fd.append("id", this.paymentForm.id);
      fd.append("payment_reference", this.paymentForm.payment_reference);
      fd.append("payment_date", this.paymentForm.payment_date);
      execute(fd, authUrl).then((res) => {
        this.saving = false;
        if (res.data && res.data.success) {
          this.showPaymentModal = false;
          this.loadClaims();
        } else {
          alert(res.data && res.data.message ? res.data.message : "Save failed");
        }
      }).catch(() => { this.saving = false; });
    }
  }
};
</script>

<style scoped>
.director-module { padding: 0 0 24px; }
.actions-row { display: flex; gap: 12px; margin-bottom: 16px; align-items: center; }
.card-section { background: #fff; border-radius: 8px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); padding: 16px; }
.table-wrap { overflow-x: auto; }
.table { width: 100%; font-size: 13px; }
.modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.4); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.modal-card { background: #fff; border-radius: 8px; padding: 24px; max-width: 480px; width: 90%; max-height: 90vh; overflow-y: auto; }
.form-group { margin-bottom: 12px; }
.form-group label { display: block; font-weight: 600; margin-bottom: 4px; }
.modal-actions { display: flex; gap: 8px; justify-content: flex-end; margin-top: 16px; }
.text-muted { color: #6c757d; }
</style>
